<template>
    <div>
        <md-dialog md-open-from="#replacePage" md-close-to="#replacePage" ref="replacePage" @open="fixContent" @close="startOver">
            <div style="text-align: center;">
                <div style="padding: 14px;padding-bottom: 0px;" >
                    <md-dialog-content>
                        <p>Replace Page</p>
                        <md-input-container>
                            <label for="replace-page-file">File</label>
                            <md-file id="replace-page-file" :key="fileInputKey" required @selected="onReplacePageFileChange" accept="application/pdf"></md-file>
                        </md-input-container>

                        <div class="from-to-wrap">
                            <div class="from-to">
                                <md-input-container>
                                    <label for="replace-page-from">From </label>
                                    <md-input id="replace-page-from" type="input" readonly v-model="replacePageFrom"/>
                                </md-input-container>
            
                                
                                <md-input-container>
                                    <label for="replace-page-to">To </label>
                                    <md-input id="replace-page-to" type="input" readonly v-model="replacePageTo"/>
                                </md-input-container>
                            </div>
                            
                            <div class="comm-error">
                                <p v-if="errorFromMessage" class="error">{{ errorFromMessage }}</p>
                                <p v-if="errorToMessage" class="error">{{ errorToMessage }}</p>
                            </div>
                        </div>
                        
                        <md-button class="md-primary md-raised" style="margin-bottom: 25px;" @click="replacePages">Replace Page(s)</md-button>        
                    </md-dialog-content>
                </div>    
            </div>
        </md-dialog>
        <md-dialog ref="savedDraftThumbnailDialog" @close="savedDraftThumbnailDialogCloseOutside">
            <md-dialog-content style="padding: 24px; width: 800px;">
                <p style="margin-bottom: 16px;">Warning, you are about to change the PDF for all users. This cannot be undone.</p>
                <p style="font-size: 16px; font-weight: bold;">
                    Cuelist recommends creating a backup before applying any changes.
                </p>
                <p v-if="project.autoSaveCap === true" style="margin-top: 12px;">
                    Cuelist keeps your last 10 page-change saves.
                </p>
            </md-dialog-content>

            <md-dialog-actions style="display: flex; justify-content: flex-end; padding: 16px;">
                <md-button class="md-default" @click="savedDraftThumbnailDialogClose">
                    Cancel
                </md-button>
                <md-button class="md-accent" @click="onDialogClose('proceedWithoutSaving')">
                    Proceed Without Saving
                </md-button>
                <md-button class="md-primary md-raised" @click="onDialogClose('saveAndContinue')">
                    Save & Continue
                </md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import { pageOpDetail } from '@/lib/history-autosave';
import PDFJS from 'pdfjs-dist/webpack';
import {PROJECT_REPLACE_PAGES_URL} from '@/lib/URLs';
import { apiAuthHeaders } from '@/lib/api-auth-client';
import uploadPageFile from '@/lib/page-upload-client';
import { pdfTooLargeMessage, pageUploadFailed, PAGE_UPLOAD_FAILED_MESSAGE } from '@/lib/page-upload';
import { useServerPageChange } from '@/lib/server-page-change';
import pageChangeLockMode from '@/lib/page-change-lock-mode';
import { pageCountForPageOp, labelsAfterReplace } from '@/lib/page-labels';

const CreateProject = {
    name: 'replacePage',
    // Which page-change lock this window uses (lib/page-change-lock-mode.js, shipped dark 20 Oct 2026).
    mixins: [pageChangeLockMode],
    props: [
        'currentPage',
        'replace_page_from',
        'replace_page_to'
    ],
    data: function() {
        return {
            replacePageFile: null,
            replacePageFilePagesCount: 0,
            posterImage: null,
            validPoster: false,
            uploadingPoster: false,
            projectCuePlacement: this.$props.cuePlacement || 'right',
            errorFromMessage: '',
            errorToMessage: '',
            fileInputKey: Date.now(),
            isSavedDraftDialogOpen: false,
            pageChangeRunning: false,
            lockStaysUp: false
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'users',
            'project',
            'cues',
            'notes',
            'bookmarks',
            'loaderShow',
            'currentVersion',
            'projectTotalPages',
            'projectID'
        ]),
        isCreateDisabled() {
            return !(this.project.title && this.project.file && this.project.ownerRole);
        },
        replacePageFrom() {
            return this.replace_page_from && this.project.pdfPageNumbers[this.replace_page_from - 1].name;
        },
        replacePageTo() {
            return this.replace_page_to && this.project.pdfPageNumbers[this.replace_page_to - 1].name;
        },
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        // Labels and the loaded PDF must agree before the helper rebuilds the PDF
        // (7 Oct 2026, lib/page-labels.js): a short label list used to cut pages off.
        pageOpCheck() {
            return pageCountForPageOp({ labels: this.project.pdfPageNumbers, loadedPageCount: this.projectTotalPages });
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    methods: {
        validateNumber(e) {
            if (e < 1) {
                this.errorFromMessage = 'Value should be greater than or equal to 1.';
            } else if (e > this.totalPages) {
                this.errorFromMessage = `Value should be less than or equal to ${this.totalPages}.`;
            } else {
                this.errorFromMessage = '';
            }
        },
        validateToNumber(e) {
            if (e < 1) {
                this.errorToMessage = 'Value should be greater than or equal to 1.';
            } else if (e > this.totalPages) {
                this.errorToMessage = `Value should be less than or equal to ${this.totalPages}.`;
            } else {
                this.errorToMessage = '';
            }
        },
        async open() {
            this.replacePageFile = null;
            this.errorToMessage = '';
            this.fileInputKey = Date.now();
            await this.$refs.savedDraftThumbnailDialog.close();
            await this.beginPageChangeWindow();
            await this.$refs.replacePage.open();
        },
        replacePages() {
            if (this.refuseIfPagesOutOfStep()) {
                return;
            }
            if (!this.replacePageFile) {
                this.errorToMessage = 'Oops! You need to select a PDF first.';
            } else {
                this.cancel();
                this.$nextTick(async () => {
                    this.beginPageChangeWindow();
                    this.isSavedDraftDialogOpen = true;
                    await this.$refs.savedDraftThumbnailDialog.open();
                });
            }
        },
        async savedDraftThumbnailDialogClose() {
            this.isSavedDraftDialogOpen = false;
            await this.endPageChangeWindow();
            await this.$refs.savedDraftThumbnailDialog.close();
        },
        async savedDraftThumbnailDialogCloseOutside() {
            this.isSavedDraftDialogOpen = false;
            await this.endPageChangeWindow();
        },
        // Josh, 14 Oct: the lock goes on at the click, not when the window opens (lib/page-change-lock.js).
        // true = this person holds it and the change may start. 'held': someone else is changing pages and
        // their hold message shows here; anything else is an error and nothing starts.
        async lockForPageChange() {
            this.pageChangeRunning = true;
            this.lockStaysUp = false;
            this.$store.dispatch('setLoader', { type: true });
            let result;
            try {
                result = await this.$store.dispatch('takePageChangeLock');
            } catch (error) {
                console.error('[page-change] could not set the lock:', error);
                result = 'error';
            }
            if (result === 'taken') return true;
            this.pageChangeRunning = false;
            this.$store.dispatch('setLoader', { type: false });
            if (result === 'held') {
                await this.$refs.savedDraftThumbnailDialog.close();
            } else if (this.$toast) {
                // Josh's wording, 14 Oct: the lock could not be set, so nothing started.
                this.$toast.error("The page change didn't start. Nothing was changed. Please try again.");
            }
            return false;
        },
        releaseLockAfterChange() {
            this.pageChangeRunning = false;
            if (this.lockStaysUp) return Promise.resolve('kept');
            return this.$store.dispatch('releasePageChangeLock');
        },
        refuseIfPagesOutOfStep() {
            if (this.pageOpCheck.ok) {
                return false;
            }
            console.error('[page-op] refused:', this.pageOpCheck.reason);
            if (this.$toast) {
                this.$toast.error("This script's page numbering is out of step with its pages, so nothing was changed. Refresh your browser and try again. If this keeps happening, contact support.");
            }
            return true;
        },
        getFormattedVersionTitle() {
            const now = new Date();
            const date = now.toLocaleDateString('en-GB');
            const time = now.toLocaleTimeString('en-US', { hour12: true });

            const formattedDate = date.replaceAll('/', '-');
            return `PDF Adjustment – ${formattedDate} ${time}`;
        },
        async onDialogClose(type) {
            this.isSavedDraftDialogOpen = false;
            if (type === 'proceedWithoutSaving') {
                if (this.replacePageFile && this.replacePageFilePagesCount) {
                    if (!(await this.startPageChange())) return;
                    this.working = true;
                    await this.doUploadFileToReplacePages();
                    await this.finishPageChange();
                }
            } else if (type === 'saveAndContinue') {
                if (this.replacePageFile && this.replacePageFilePagesCount) {
                    if (!(await this.startPageChange())) return;
                    this.working = true;
                    try {
                        const versionTitle = this.getFormattedVersionTitle();
                        await this.$store.dispatch('createVersion', {
                            name: versionTitle,
                            autoSave: 'page_op',
                            autoSaveDetail: pageOpDetail({ op: 'replace', from: this.replacePageFrom, to: this.replacePageTo })
                        });
                        await this.$store.dispatch('pruneAutoSaves', { kind: 'page_op' });
                        await this.doUploadFileToReplacePages();
                        await this.finishPageChange();
                    } catch (error) {
                        console.error('Error saving version:', error);
                        this.afterFailedSave();
                        this.$store.dispatch('setLoader', { type: false });
                        this.working = false;
                        this.$toast.error('Failed to save version. Please try again.');
                    }
                }
            }
        },
        async doUploadFileToReplacePages() {
            const regexp = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/;
            const matches = regexp.exec(this.project.documentUrl);

            if (!matches || !matches[1]) {
                console.error('Invalid document URL');
                return;
            }

            try {
                const pageOp = this.pageOpCheck;
                if (!pageOp.ok) throw new Error('Page op refused: ' + pageOp.reason);
                this.working = true;
                this.$store.dispatch('setLoader', { type: true });

                const formData = new FormData();
                formData.append('project_id', this.project.id);
                formData.append('documentUrl', this.project.documentUrl);
                formData.append('filePath', decodeURIComponent(matches[1]));
                formData.append('replace_page_from', this.replace_page_from);
                formData.append('replace_page_to', this.replace_page_to);
                formData.append('totalPages', pageOp.total);
                // The PDF goes to Storage first; the helper is sent only its path (big PDFs, 26 Oct 2026).
                formData.append('uploadPath', await uploadPageFile(this.project.id, this.replacePageFile));

                let response;
                try {
                    response = await fetch(PROJECT_REPLACE_PAGES_URL, {
                        method: 'POST',
                        headers: await apiAuthHeaders(PROJECT_REPLACE_PAGES_URL),
                        body: formData
                    });
                } catch (e) {
                    throw pageUploadFailed(e);
                }
                if (response.status === 413) throw pageUploadFailed(new Error('413 from the helper'));

                if (!response.ok) throw new Error('Failed to upload file');

                const replacingPages = (Number(this.replace_page_to) - Number(this.replace_page_from)) + 1;

                // Shows switched to the server renumber (lib/server-page-change.js): one call instead of the writes below.
                if (this.replacePageFilePagesCount > 0 && useServerPageChange(this.project)) {
                    await this.$store.dispatch('applyPageChangeOnServer', {
                        op: 'replace',
                        params: { from: Number(this.replace_page_from), to: Number(this.replace_page_to), count: this.replacePageFilePagesCount },
                        pagesBefore: pageOp.total
                    });
                    // Only after the server returns (14 Oct 2026; see delete-page.vue finishDeleteOnServer).
                    this.$store.dispatch('updateShowThumbnailPageUpdate', true);
                    NavBus.$emit('reloadCurrentProject', { newPagePosition: Number(this.replace_page_from) });
                    this.$store.dispatch('updateShowThumbnailPageUpdate', false);
                } else if (this.replacePageFilePagesCount > 0 && this.project.pdfPageNumbers.length > 0) {
                    this.$store.dispatch('updateShowThumbnailPageUpdate', true);
                    // Fewer pages now drop the labels of the pages that went away (7 Oct 2026),
                    // so the list always matches the new PDF and the next page op is allowed.
                    const updatedPdfPages = labelsAfterReplace(this.project.pdfPageNumbers, {
                        from: this.replace_page_from,
                        to: this.replace_page_to,
                        newCount: this.replacePageFilePagesCount
                    });
                    await this.$store.dispatch('renamePdfPages', { pdfPage: updatedPdfPages });

                    const pageOffset = this.replacePageFilePagesCount - replacingPages;

                    const cuesToUpdate = [];
                    const cuesToDelete = [];
                    this.cues.forEach((cue) => {
                        if (pageOffset > 0 && cue.pageNumber > Number(this.replace_page_to)) {
                            cue.pageNumber += pageOffset;
                            cuesToUpdate.push(cue);
                        } else if (pageOffset < 0 && cue.pageNumber > Number(this.replace_page_to)) {
                            cue.pageNumber += pageOffset;
                            cuesToUpdate.push(cue);
                        } else if (pageOffset < 0 && cue.pageNumber >= Number(this.replace_page_from) + this.replacePageFilePagesCount) {
                            cuesToDelete.push(cue);
                        }
                        // Unchanged cues are intentionally NOT written. A same-count
                        // replace (pageOffset 0) touches zero cues, avoiding a mass
                        // redundant write + read-back that froze clients on dense projects.
                    });
                    // Batch the cue writes: a page-op shifts pageNumber on every cue after
                    // the change point; on a dense project that was ~1,000+ individual
                    // updateCue writes at once, which made the op crawl. bulkUpdateCues/
                    // bulkDeleteCues chunk them into ~490-op batches.
                    const updateCuePromises = [
                        this.$store.dispatch('bulkUpdateCues', cuesToUpdate),
                        this.$store.dispatch('bulkDeleteCues', cuesToDelete)
                    ];

                    const updateNotePromises = this.notes.map((note) => {
                        if (pageOffset > 0 && note.page > Number(this.replace_page_to)) {
                            note.page += pageOffset;
                        } else if (pageOffset < 0 && note.page > Number(this.replace_page_to)) {
                            note.page += pageOffset;
                        } else if (pageOffset < 0 && note.page >= Number(this.replace_page_from) + this.replacePageFilePagesCount) {
                            return this.$store.dispatch('removeUserNote', { note });
                        }
                        return this.$store.dispatch('updateUserNote', { note });
                    });

                    // Bookmarks — collect moves/removes and apply them in one atomic
                    // batch (see moveBookmarksBulk); per-move updates raced and could
                    // wipe each other when several bookmarks shifted by the same offset.
                    const bookmarkMoves = [];
                    const bookmarkRemoves = [];
                    Object.keys(this.bookmarks).forEach((bookmarkPage) => {
                        if (pageOffset !== 0 && Number(bookmarkPage) > Number(this.replace_page_to)) {
                            bookmarkMoves.push({ from: bookmarkPage, to: Number(bookmarkPage) + pageOffset });
                        } else if (pageOffset < 0 && Number(bookmarkPage) >= Number(this.replace_page_from) + this.replacePageFilePagesCount) {
                            bookmarkRemoves.push(bookmarkPage);
                        }
                    });
                    const updateBookmarkPromise = this.$store.dispatch('moveBookmarksBulk', { moves: bookmarkMoves, removes: bookmarkRemoves });

                    // Act/Scene/Song markers key off `pageIndex`, not `pageNumber`, so they
                    // need their own adjustment — drop markers on pages the replacement
                    // dropped, shift the rest. Mirrors the project-settings nav replace path.
                    const adjustMarkersPromise = this.$store.dispatch('adjustScriptMarkersAfterPageReplace', {
                        replaceFrom: this.replace_page_from,
                        replaceTo: this.replace_page_to,
                        newPageCount: this.replacePageFilePagesCount
                    });

                    await Promise.all([
                        ...updateCuePromises,
                        ...updateNotePromises,
                        updateBookmarkPromise,
                        adjustMarkersPromise
                    ]);
                    this.$store.dispatch('notifyNotesListStructureIfLinked');

                    const newPagePosition = Number(this.replace_page_from);
                    NavBus.$emit('reloadCurrentProject', { newPagePosition });
                    this.$store.dispatch('updateShowThumbnailPageUpdate', false);
                }

                this.replacePageFile = null;
                this.replacePageFilePagesCount = 0;
                this.working = false;
                this.$store.dispatch('setLoader', { type: false });
            } catch (error) {
                console.error(error);
                // A server change that stopped part way after every retry keeps the lock up (Josh, 14 Oct).
                if (error && error.stoppedPartWay) this.lockStaysUp = true;
                this.replacePageFile = null;
                this.replacePageFilePagesCount = 0;
                this.working = false;
                this.$store.dispatch('setLoader', { type: false });
                if (this.$toast) this.$toast.error(error && error.pageUploadFailed ? PAGE_UPLOAD_FAILED_MESSAGE : 'Something went wrong updating the pages. Please reload and check the affected pages.');
            }
        },
        onReplacePageFileChange: async function(fileList) {
            this.replacePageFile = fileList[0];
            const tooLarge = this.replacePageFile && pdfTooLargeMessage(this.replacePageFile.size);
            if (tooLarge) {
                this.replacePageFile = null;
                this.replacePageFilePagesCount = 0;
                this.fileInputKey = Date.now();
                if (this.$toast) this.$toast.error(tooLarge);
                return;
            }
            if (this.replacePageFile) {
                const arrayBuffer = await this.replacePageFile.arrayBuffer();
                PDFJS.getDocument({data: new Uint8Array(arrayBuffer)}).then((pdf) => {
                    this.replacePageFilePagesCount = pdf.pdfInfo.numPages;
                });
                this.errorToMessage = '';
            }
        },
        fixContent() {
            if (this.$refs.stepper) {
                this.$nextTick(() => {
                    this.$refs.stepper.calculateStepsWidthAndPosition();
                });
            }
        },
        startOver() {
            setTimeout(async () => {
                if (!this.isSavedDraftDialogOpen) {
                    await this.endPageChangeWindow();
                }
            }, 1200);
        },
        cancel() {
            this.$refs.replacePage.close();
        }
    },
    components: {
    }
};
export default CreateProject;
</script>

<style>
.md-dialog {
    min-width: 400px;
}


.from-to {
    display: flex;
    justify-content: space-between;
}
.from-to > * {
    width: 100%;
    max-width: 47%;
}
.error {
    color: red;
}
</style>


// WEBPACK FOOTER //
// replace-page.vue?41815d88